import { eventQueryCommandSelector } from './../../ngrx.stores/event.query.command/selectors';
import { VisitorEvent } from './../../../shared/models/visitor.event';
import { Observable, switchMap, of, map, takeUntil } from 'rxjs';
import { VisitorEventService } from './../../services/visitor.event.service';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from './../../../../common/components/base.component';
import {
  Component,
  ViewEncapsulation,
  OnInit,
  Renderer2,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CalendarQueryCommand } from 'src/app/modules/shared/models/calendar.action.command';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { select, Store } from '@ngrx/store';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { IMapMarkerWithCustomData } from 'src/app/modules/shared/models/place.marker';

@Component({
  selector: 'ig-visitor-event-map',
  templateUrl: './visitor.event.map.component.html',
  styleUrls: ['./visitor.event.map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VisitorEventMapComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  queryCommand: CalendarQueryCommand = new CalendarQueryCommand();
  eventsAndMarkers: {
    events: VisitorEvent[];
    markers: IMapMarkerWithCustomData<VisitorEvent>[];
  };
  eventsRowScrolllistener: () => void = null;
  isShowDetails = false;
  showingEvent: VisitorEvent = null;
  mapCenter: { lat: number; lng: number } = null;
  isLoading = true;
  @ViewChild('eventsRow', { static: false }) eventsRow: ElementRef;
  @ViewChildren('event') eventEles: QueryList<ElementRef>;
  @ViewChild(GoogleMapComponent, { static: false }) map: GoogleMapComponent;

  isDesktopSm = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    private _eventService: VisitorEventService,
    private _renderer2: Renderer2,
    private _store: Store<IAppState>,
    private _mediaQueryService: MediaQueryService
  ) {
    super({ activatedRoute });

    this._mediaQueryService.isMobile$.subscribe((isDesktopSm) => {
      this.isDesktopSm = isDesktopSm;
    });
  }

  ngOnInit(): void {
    this.queryCommand.isForMapView = true;
    this.queryCommand.limit = 9999;
    this.queryCommand.page = 1;

    this._store
      .pipe(takeUntil(this.isDestroyed), select(eventQueryCommandSelector))
      .subscribe((cmd) => {
        this._eventService
          .getEvents(this.hubName, { ...cmd, ...this.queryCommand })
          .pipe(
            switchMap((events) =>
              this.createMapMarkersForEvents$(events).pipe(
                map((markers) => ({ events, markers }))
              )
            )
          )
          .subscribe((data) => {
            this.eventsAndMarkers = data;
            this.isLoading = false;
            if (!data.events?.length) {
              this.map.setNoContent();
            }
          });
      });
  }

  override ngOnDestroy() {
    this.eventsRowScrolllistener && this.eventsRowScrolllistener();
  }

  showDetails(event: VisitorEvent) {
    this.isShowDetails = true;
    this.showingEvent = event;

    this.mapCenter = !this.showingEvent.isOnlineEvent
      ? {
          lat: this.showingEvent.address.lat,
          lng: this.showingEvent.address.lng,
        }
      : null;

    this.eventsAndMarkers.markers.forEach((x) => {
      if (x.title !== event._id) {
        x.options = {
          icon: null,
          animation: null,
          zIndex: 0,
        };
      } else {
        x.options = {
          icon: 'https://iti-images.s3.amazonaws.com/imgs/marker-xs.png',
          animation: google.maps.Animation.BOUNCE,
          zIndex: 2,
        };
      }
    });
  }

  markerClicked(marker: IMapMarkerWithCustomData<VisitorEvent>) {
    this.showDetails(marker.data);
    this.eventEleScrollIntoView(marker);
  }

  eventEleScrollIntoView(marker: IMapMarkerWithCustomData<VisitorEvent>) {
    const eventEle = this.eventEles.find(
      (x) => x.nativeElement.attributes['data-id'].value === marker.data._id
    );
    eventEle &&
      eventEle.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
  }

  createMapMarkersForEvents$(
    events: VisitorEvent[]
  ): Observable<IMapMarkerWithCustomData<VisitorEvent>[]> {
    return of(
      events
        .filter((x) => !x.isOnlineEvent && x.address?.lat && x.address?.lng)
        .map((e) => {
          return {
            position: {
              lat: e.address?.lat,
              lng: e.address?.lng,
            },
            title: e._id,
            data: e,
          } as IMapMarkerWithCustomData<VisitorEvent>;
        })
    );
  }
}
